import React, { useState, useEffect } from "react";
import { Outlet, useNavigate, useLocation, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useRealtime } from "../context/RealtimeContext";
import { api } from "../lib/api";
import { Avatar } from "./Avatar";
import { NotificationsBell } from "./NotificationsBell";
import { Input } from "@/components/ui/input";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Home, Users, MessageCircle, Search, Shield, LogOut, User as UserIcon, LifeBuoy, ShieldCheck, Compass, PawPrint, Crown, Settings, Circle, Palette, Menu, Sparkles, UserPlus, Share2 } from "lucide-react";
import { LOGO } from "../lib/api";
import { useSiteContent } from "../context/SiteContentContext";
import { InviteDialog } from "./InviteFriends";
import { ShareCard } from "./ShareCard";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

function WelcomeIntro({ open, onClose }) {
  const { content } = useSiteContent();
  const paras = (content.welcome_popup_body || "").split(/\n\n+/).filter(Boolean);
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="border-4 border-black bg-spotter-cream max-h-[90dvh] overflow-y-auto overscroll-contain top-[5dvh] translate-y-0" data-testid="welcome-intro-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-3xl text-spotter-maroon flex items-center gap-2"><PawPrint className="w-7 h-7" style={{ color: "#E19C00" }} />{content.welcome_popup_title}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 text-[15px] leading-relaxed">
          {paras.map((p, i) => <p key={i} className={i === paras.length - 1 ? "font-semibold text-spotter-maroon" : ""}>{p}</p>)}
        </div>
        <DialogFooter>
          <Button data-testid="welcome-intro-close" onClick={onClose} className="bg-spotter-maroon text-spotter-cream border-2 border-black font-heading font-bold">Let's go! 🐆</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function NavIcon({ to, icon: Icon, label, badge, testid }) {
  const navigate = useNavigate();
  const loc = useLocation();
  const active = loc.pathname.startsWith(to);
  return (
    <button data-testid={testid} onClick={() => navigate(to)} title={label}
      className={`relative p-2 rounded-lg transition-colors ${active ? "bg-spotter-gold text-black" : "text-spotter-cream hover:bg-white/15"}`}>
      <Icon className="w-6 h-6" />
      {badge > 0 && (
        <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 text-[11px] font-bold rounded-full bg-spotter-gold text-black border border-black flex items-center justify-center">{badge > 99 ? "99+" : badge}</span>
      )}
    </button>
  );
}

export function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const loc = useLocation();
  const isProfile = loc.pathname.startsWith("/profile");
  const { content } = useSiteContent();
  const rt = useRealtime();
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [dmCount, setDmCount] = useState(0);
  const [showWelcome, setShowWelcome] = useState(false);
  const [onlineCount, setOnlineCount] = useState(0);
  const [reportCount, setReportCount] = useState(0);
  const [onlineOpen, setOnlineOpen] = useState(false);
  const [onlineList, setOnlineList] = useState([]);
  const [onlineScope, setOnlineScope] = useState("pride");
  const [inviteOpen, setInviteOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const openOnline = async () => {
    setOnlineOpen(true);
    try { const { data } = await api.get("/presence/online"); setOnlineList(data.online || []); setOnlineScope(data.scope || "pride"); }
    catch (e) { setOnlineList([]); }
  };

  useEffect(() => {
    if (!user?.is_admin) return;
    const fetchReports = async () => { try { const { data } = await api.get("/reports/unread-count"); setReportCount(data.count); } catch (e) {} };
    fetchReports();
    const t = setInterval(fetchReports, 20000);
    return () => clearInterval(t);
  }, [user]);

  useEffect(() => {
    const fetchCount = async () => { try { const { data } = await api.get("/presence/count"); setOnlineCount(data.online); } catch (e) {} };
    fetchCount();
    const t = setInterval(fetchCount, 20000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    if (localStorage.getItem("spotter_show_welcome") === "1") {
      setShowWelcome(true);
      localStorage.removeItem("spotter_show_welcome");
    }
  }, []);

  const refreshDm = async () => {
    try {
      const { data } = await api.get("/conversations");
      setDmCount(data.reduce((a, c) => a + c.unread, 0));
    } catch (e) {}
  };

  useEffect(() => { refreshDm(); }, []);
  useEffect(() => { if (rt) return rt.registerConvHandler(refreshDm); }, [rt]);

  useEffect(() => {
    if (!q.trim()) { setResults([]); return; }
    const t = setTimeout(async () => {
      try { const { data } = await api.get(`/users/search?q=${encodeURIComponent(q)}`); setResults(data); } catch (e) {}
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  return (
    <div className="min-h-screen leopard-frame">
      <WelcomeIntro open={showWelcome} onClose={() => setShowWelcome(false)} />
      <InviteDialog open={inviteOpen} onOpenChange={setInviteOpen} />
      <ShareCard user={user} open={shareOpen} onClose={() => setShareOpen(false)} />
      <Dialog open={onlineOpen} onOpenChange={setOnlineOpen}>
        <DialogContent className="border-4 border-black bg-spotter-cream max-h-[80dvh] overflow-y-auto" data-testid="online-members-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl text-spotter-maroon flex items-center gap-2">
              <Circle className="w-4 h-4" style={{ fill: "#2ecc71", color: "#2ecc71" }} />Online now
            </DialogTitle>
          </DialogHeader>
          <p className="text-xs text-spotter-maroon/70 -mt-2">{onlineScope === "all" ? "Everyone online across Spotter." : "Members of your Pride who are online."}</p>
          {onlineList.length === 0 ? (
            <p className="text-sm text-spotter-maroon py-6 text-center" data-testid="online-empty">{onlineScope === "all" ? "No one else is online right now." : "None of your Pride are online right now."}</p>
          ) : (
            <div className="space-y-2">
              {onlineList.map((u) => (
                <button key={u.id} data-testid={`online-member-${u.id}`} onClick={() => { navigate(`/profile/${u.id}`); setOnlineOpen(false); }}
                  className="w-full flex items-center gap-3 p-2 rounded-xl border-2 border-black bg-white text-left hover:bg-spotter-cream">
                  <Avatar user={u} size="md" showPresence />
                  <div className="flex-1 min-w-0"><div className="font-bold text-sm truncate">{u.name}</div><div className="text-xs text-spotter-maroon">@{u.username}</div></div>
                  <Circle className="w-2.5 h-2.5" style={{ fill: "#2ecc71", color: "#2ecc71" }} />
                </button>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
      <header className="sticky top-0 z-40 border-b-4 border-black" style={{ background: "#520202" }}>
        <div className="max-w-6xl mx-auto px-4 h-16 flex items-center gap-3">
          <Link to="/feed" data-testid="logo-home" className="shrink-0">
            <img src={LOGO} alt="Spotter" className="h-11 sm:h-12 w-auto" />
          </Link>

          <div className="relative flex-1 max-w-xs hidden sm:block">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-spotter-maroon" />
            <Input data-testid="search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Spot Search — people & posts…"
              className="pl-9 bg-spotter-cream border-2 border-black h-10" />
            {results.length > 0 && (
              <div className="absolute mt-1 w-full bg-white border-2 border-black rounded-lg overflow-hidden z-50 brand-shadow-sm">
                {results.map((u) => (
                  <button key={u.id} data-testid={`search-result-${u.id}`}
                    onClick={() => { navigate(`/profile/${u.id}`); setQ(""); setResults([]); }}
                    className="w-full flex items-center gap-2 p-2 hover:bg-spotter-cream text-left">
                    <Avatar user={u} size="sm" showPresence />
                    <div><div className="font-semibold text-sm">{u.name}</div><div className="text-xs text-spotter-maroon">@{u.username}</div></div>
                  </button>
                ))}
              </div>
            )}
          </div>
          <button data-testid="online-counter" onClick={openOnline} title="See who's online now" className="hidden md:flex items-center gap-1.5 bg-black/30 hover:bg-black/50 transition-colors text-spotter-cream rounded-full px-3 py-1.5 text-sm font-bold cursor-pointer">
            <Circle className="w-3 h-3" style={{ fill: "#2ecc71", color: "#2ecc71" }} /> {onlineCount} online
          </button>

          <nav className="flex items-center gap-1 ml-auto">
            <button data-testid="nav-spotchat-icon" title="Spot Chat" onClick={() => navigate("/messages")} className="relative p-2 rounded-lg text-spotter-cream hover:bg-white/15">
              <MessageCircle className="w-6 h-6" />
              {dmCount > 0 && <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-[16px] px-0.5 text-[10px] font-bold rounded-full bg-spotter-gold text-black border border-black flex items-center justify-center">{dmCount > 99 ? "99+" : dmCount}</span>}
            </button>
            <NotificationsBell />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="hamburger-trigger" title="Menu" className="relative ml-1 p-2 rounded-lg text-spotter-cream hover:bg-white/15">
                  <Menu className="w-6 h-6" />
                  {(user?.is_admin ? reportCount : 0) > 0 && <span className="absolute -top-1 -right-1 min-w-[16px] h-[16px] px-0.5 text-[10px] font-bold rounded-full bg-spotter-gold text-black border border-black flex items-center justify-center">•</span>}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="border-2 border-black bg-spotter-cream w-56">
                <DropdownMenuItem data-testid="nav-feed" onClick={() => navigate("/feed")}><Home className="w-4 h-4 mr-2" />The Prowl</DropdownMenuItem>
                <DropdownMenuItem data-testid="nav-about" onClick={() => navigate("/welcome")}><Crown className="w-4 h-4 mr-2" />About Us</DropdownMenuItem>
                <DropdownMenuItem data-testid="nav-spots" onClick={() => navigate("/spots")}><Compass className="w-4 h-4 mr-2" />Hot Spots</DropdownMenuItem>
                <DropdownMenuItem data-testid="nav-spotlight-den" onClick={() => navigate("/spotlight-den")}><Sparkles className="w-4 h-4 mr-2" />Spotlight Den</DropdownMenuItem>
                <DropdownMenuItem data-testid="nav-invite" onClick={() => setInviteOpen(true)}><UserPlus className="w-4 h-4 mr-2" />Invite Friends</DropdownMenuItem>
                <DropdownMenuItem data-testid="nav-subscribe" onClick={() => navigate("/subscribe")}><Crown className="w-4 h-4 mr-2" />Premium Memberships</DropdownMenuItem>
                {(user?.is_admin || user?.role === "owner" || user?.role === "moderator") && <DropdownMenuItem data-testid="nav-admin" onClick={() => navigate("/admin")}><Shield className="w-4 h-4 mr-2" />{user?.is_admin || user?.role === "owner" ? "Admin Inbox" : "Team Inbox"}{reportCount > 0 && <span className="ml-auto min-w-[18px] h-[18px] px-1 text-[11px] font-bold rounded-full bg-spotter-maroon text-spotter-cream flex items-center justify-center">{reportCount > 99 ? "99+" : reportCount}</span>}</DropdownMenuItem>}
              </DropdownMenuContent>
            </DropdownMenu>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="profile-menu-trigger" className="ml-1 rounded-full"><Avatar user={user} size="sm" showPresence /></button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="border-2 border-black bg-spotter-cream w-52">
                <DropdownMenuItem data-testid="menu-profile" onClick={() => navigate(`/profile/${user.id}`)}><UserIcon className="w-4 h-4 mr-2" />My Profile</DropdownMenuItem>
                <DropdownMenuItem data-testid="menu-settings" onClick={() => navigate("/settings")}><Settings className="w-4 h-4 mr-2" />Settings</DropdownMenuItem>
                <DropdownMenuItem data-testid="menu-friends" onClick={() => navigate("/friends")}><Users className="w-4 h-4 mr-2" />Pride</DropdownMenuItem>
                <DropdownMenuItem data-testid="menu-share-spotter" onClick={() => setShareOpen(true)}><Share2 className="w-4 h-4 mr-2" />Share my Spotter</DropdownMenuItem>
                {user?.requires_guardian && (
                  <DropdownMenuItem data-testid="menu-guardian-note" disabled className="text-xs">Guardian-protected account</DropdownMenuItem>
                )}
                <DropdownMenuItem data-testid="menu-guardian" onClick={() => navigate("/guardian")}><ShieldCheck className="w-4 h-4 mr-2" />Guardian Approvals</DropdownMenuItem>
                <DropdownMenuItem data-testid="menu-help" onClick={() => navigate("/help")}><LifeBuoy className="w-4 h-4 mr-2" />Contact Admin</DropdownMenuItem>
                {user?.is_admin && (
                  <DropdownMenuItem data-testid="menu-site-editor" onClick={() => navigate("/site-editor")}><Palette className="w-4 h-4 mr-2" />Site Content</DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem data-testid="menu-logout" onClick={() => { logout(); navigate("/"); }} className="text-red-700"><LogOut className="w-4 h-4 mr-2" />Log Out</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </nav>
        </div>
      </header>

      {isProfile ? (
        <main className="py-6" data-testid="profile-main">
          <Outlet context={{ refreshDm }} />
          <footer className="text-center py-6 mt-2">
            <div className="inline-flex flex-wrap items-center justify-center gap-x-3 gap-y-1 bg-black/70 text-spotter-cream rounded-full px-5 py-2 font-heading text-sm">
              <span data-testid="footer-copyright">{content.footer_text}</span>
              <span className="opacity-50">·</span>
              <Link to="/terms" data-testid="footer-terms" className="underline hover:no-underline">Terms</Link>
              <Link to="/privacy" data-testid="footer-privacy" className="underline hover:no-underline">Privacy</Link>
              <Link to="/report" data-testid="footer-report" className="underline hover:no-underline">Report / DMCA</Link>
            </div>
          </footer>
        </main>
      ) : (
        <main className="max-w-5xl mx-auto px-3 sm:px-6 py-6">
          <div className="bg-spotter-cream border-4 border-black rounded-2xl min-h-[70vh] p-4 sm:p-6 brand-shadow">
            <Outlet context={{ refreshDm }} />
          </div>
          <footer className="text-center py-6 mt-2">
            <div className="inline-flex flex-wrap items-center justify-center gap-x-3 gap-y-1 bg-black/70 text-spotter-cream rounded-full px-5 py-2 font-heading text-sm">
              <span data-testid="footer-copyright">{content.footer_text}</span>
              <span className="opacity-50">·</span>
              <Link to="/terms" data-testid="footer-terms" className="underline hover:no-underline">Terms</Link>
              <Link to="/privacy" data-testid="footer-privacy" className="underline hover:no-underline">Privacy</Link>
              <Link to="/report" data-testid="footer-report" className="underline hover:no-underline">Report / DMCA</Link>
            </div>
          </footer>
        </main>
      )}
    </div>
  );
}
